/**
 * API Token State Management
 *
 * Provides reactive state for managing API tokens.
 */

import { ref, type Ref } from 'vue'
import ApiTokenApi from '@/models/ApiToken/Api'
import type { ApiToken, ApiTokenStorePayload } from '@/models/ApiToken/Types'

export class ApiTokenState {
	api = new ApiTokenApi()
	tokens: Ref<ApiToken[]> = ref([])
	loading: Ref<boolean> = ref(false)
	error: Ref<string | null> = ref(null)

	/**
	 * Fetch all tokens for the current user.
	 */
	async fetchTokens(): Promise<void> {
		this.loading.value = true
		this.error.value = null

		try {
			const response = await this.api.list()
			this.tokens.value = response.data.data
		} catch (e: any) {
			this.error.value = e.response?.data?.message || 'Failed to fetch tokens'
			throw e
		} finally {
			this.loading.value = false
		}
	}

	/**
	 * Create a new token.
	 * Returns the plain text token (only available at creation time).
	 */
	async createToken(params: ApiTokenStorePayload): Promise<string> {
		this.loading.value = true
		this.error.value = null

		try {
			const response = await this.api.store(params)
			// Add the new token to the list (without the plain text value)
			this.tokens.value.unshift(response.data.token)
			return response.data.plain_text_token
		} catch (e: any) {
			this.error.value = e.response?.data?.message || 'Failed to create token'
			throw e
		} finally {
			this.loading.value = false
		}
	}

	/**
	 * Delete a token.
	 */
	async deleteToken(id: string): Promise<void> {
		this.loading.value = true
		this.error.value = null

		try {
			await this.api.destroy(id)
			this.tokens.value = this.tokens.value.filter((token) => token.id !== id)
		} catch (e: any) {
			this.error.value = e.response?.data?.message || 'Failed to delete token'
			throw e
		} finally {
			this.loading.value = false
		}
	}
}

export function useApiTokenState() {
	return new ApiTokenState()
}
